import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../../lib/api";
import { formatGBP } from "../../lib/format";
import { Bank, ArrowSquareOut, CheckCircle, Warning, Clock } from "@phosphor-icons/react";

/**
 * Vendor payouts tab. Shows Stripe Connect onboarding status, provides
 * "Set up payouts" / "Continue onboarding" / "Open Stripe dashboard" buttons,
 * and lists recent payout events.
 */
export default function VendorPayouts() {
  const [status, setStatus] = useState(null);
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, p] = await Promise.all([
        api.get("/vendors/me/stripe/status"),
        api.get("/vendors/me/stripe/payouts"),
      ]);
      setStatus(s.data);
      setPayouts(p.data || []);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("[VendorPayouts] load failed:", err);
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const startOnboarding = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/vendors/me/stripe/onboard", {
        origin_url: window.location.origin,
      });
      if (data?.onboarding_url) {
        window.location.href = data.onboarding_url;
      } else {
        toast.error("Could not create onboarding link");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Onboarding failed");
    }
    setBusy(false);
  };

  const openDashboard = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/vendors/me/stripe/dashboard-link");
      if (data?.url) window.open(data.url, "_blank", "noopener,noreferrer");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not open Stripe dashboard");
    }
    setBusy(false);
  };

  if (loading) return <p className="text-sm text-[#5C5C54]">Loading payout status...</p>;
  if (!status) return null;

  const notStarted = !status.stripe_account_id;
  const complete = status.onboarding_complete && status.charges_enabled && status.payouts_enabled;
  const inProgress = status.stripe_account_id && !complete;

  return (
    <div className="max-w-3xl space-y-6" data-testid="vendor-payouts-panel">
      <div className="card-surface p-6">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-2xl bg-[#F2EFE9] flex items-center justify-center flex-shrink-0">
            <Bank size={22} weight="duotone" className="text-[#A94438]" />
          </div>
          <div className="flex-1">
            <h2 className="font-heading text-xl font-bold mb-1">Payouts &amp; Payments</h2>
            <p className="text-sm text-[#5C5C54] mb-4">
              AfriPantry uses Stripe to route customer payments directly to your bank account.
              We take a 10% platform fee on each order; delivery fees pass through to you 100%.
            </p>

            {notStarted && (
              <div data-testid="onboarding-not-started" className="p-4 bg-[#E4A951]/10 border border-[#E4A951]/40 rounded-xl mb-4">
                <div className="flex items-center gap-2 mb-1">
                  <Warning size={18} weight="fill" className="text-[#8A6A1F]" />
                  <p className="text-sm font-bold text-[#8A6A1F]">Set up payouts to start receiving orders</p>
                </div>
                <p className="text-xs text-[#5C5C54]">You'll need your UK business details, UTR (tax reference), photo ID, and UK bank account.</p>
              </div>
            )}

            {inProgress && (
              <div data-testid="onboarding-in-progress" className="p-4 bg-[#E4A951]/10 border border-[#E4A951]/40 rounded-xl mb-4">
                <div className="flex items-center gap-2 mb-1">
                  <Clock size={18} weight="fill" className="text-[#8A6A1F]" />
                  <p className="text-sm font-bold text-[#8A6A1F]">Onboarding in progress</p>
                </div>
                <p className="text-xs text-[#5C5C54]">Continue to Stripe to finish verification. Your payouts will unlock once approved.</p>
                {status.requirements_due?.length > 0 && (
                  <ul className="text-xs text-[#5C5C54] mt-2 list-disc pl-5">
                    {status.requirements_due.slice(0, 5).map((r) => (
                      <li key={r}>{r.replace(/_/g, " ").replace(/\./g, " › ")}</li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            {complete && (
              <div data-testid="onboarding-complete" className="p-4 bg-[#2B5329]/10 border border-[#2B5329]/30 rounded-xl mb-4">
                <div className="flex items-center gap-2">
                  <CheckCircle size={18} weight="fill" className="text-[#2B5329]" />
                  <p className="text-sm font-bold text-[#2B5329]">Payouts active — you're ready to receive orders</p>
                </div>
              </div>
            )}

            <div className="flex flex-wrap gap-2">
              {notStarted && (
                <button
                  data-testid="btn-start-onboarding"
                  onClick={startOnboarding}
                  disabled={busy}
                  className="btn-primary disabled:opacity-50"
                >
                  {busy ? "Redirecting..." : "Set up payouts"}
                </button>
              )}
              {inProgress && (
                <button
                  data-testid="btn-continue-onboarding"
                  onClick={startOnboarding}
                  disabled={busy}
                  className="btn-primary disabled:opacity-50"
                >
                  {busy ? "Redirecting..." : "Continue onboarding"}
                </button>
              )}
              {complete && (
                <button
                  data-testid="btn-open-stripe-dashboard"
                  onClick={openDashboard}
                  disabled={busy}
                  className="btn-secondary gap-2"
                >
                  Open Stripe dashboard <ArrowSquareOut size={14} weight="bold" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="card-surface p-6" data-testid="vendor-payouts-history">
        <h3 className="font-heading text-lg font-bold mb-4">Recent payouts</h3>
        {payouts.length === 0 && (
          <p className="text-sm text-[#5C5C54]">
            No payouts yet. Your payouts will appear here once Stripe sends them to your bank account
            (typically within 2–3 business days of a completed order).
          </p>
        )}
        {payouts.length > 0 && (
          <div className="space-y-2">
            {payouts.map((p) => (
              <div key={p.payout_id} className="flex items-center justify-between py-2 border-b border-[#E5E2D9] last:border-0">
                <div>
                  <p className="font-bold text-sm">{formatGBP(p.amount)} {p.currency}</p>
                  <p className="text-xs text-[#5C5C54]">
                    {p.status === "paid" ? "Paid" : "Failed"} · {new Date(p.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
                  </p>
                </div>
                <span className={`chip capitalize ${p.status === "paid" ? "bg-[#2B5329]/10 text-[#2B5329]" : "bg-[#A94438]/10 text-[#A94438]"}`}>
                  {p.status}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
